/**
 * A random (version 4) uuid: the only shape the server accepts for each of the
 * three ids an event carries — its own, its session's and its attempt's.
 *
 * `crypto.randomUUID()` needs a secure context and a browser from 2021 or
 * later. `getRandomValues()` has neither restriction, so the fallback builds
 * one from that. The fallbacks this replaces produced timestamp-and-random
 * strings that were not uuids, and the server rejects an event with any id
 * that is not a uuid — so on that path every event was dropped at ingest.
 *
 * One generator for all three on purpose. Fixing the fallback for two of the
 * ids still left the session id to get every event rejected (Copilot).
 */
export function uuid(): string {
  try {
    return crypto.randomUUID()
  } catch {
    const bytes = crypto.getRandomValues(new Uint8Array(16))

    // RFC 9562: version 4 in the high nibble of byte 6, variant 0b10 in the
    // top two bits of byte 8.
    bytes[6] = (bytes[6] & 0x0F) | 0x40
    bytes[8] = (bytes[8] & 0x3F) | 0x80

    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')

    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
  }
}

/** Whether a value has the shape the server's uuid check accepts: any version, either case. */
export function isUuid(value: string): boolean {
  return /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i.test(value)
}
